import AudioTrack from './lib/AudioTrack.js'
import SceneVisualizer from './lib/SceneVisualizer.js'
import { GLOBAL_CONSTANTS } from 'assets/js/utils/constants'

const SELECTORS = {
  visualizer: '.js-visualizer',
  visContainer: '.js-bioacoustics-visualizer__vis-container',
  checkbox: '.js-checkbox',
  skipLink: '.bioacoustics-visualizer__skip-link'
}

class Visualizer {
  constructor (element) {
    this.element = element
    this.visContainer = this.element.querySelector(SELECTORS.visContainer)
    this.skipLink = this.element.querySelector(SELECTORS.skipLink)
    this.tracks = []
    this.visualizer = new SceneVisualizer(this.visContainer)
    this.motionButton = window.unlocked.toggleButtons[this.element.dataset.motionToggle]
    this.audioButton = window.unlocked.toggleButtons[this.element.dataset.audioToggle]
    this.emitter = GLOBAL_CONSTANTS.UTILS.EMITTER
    this.firstInteraction = true
    this.isVisible = false
    this.prevVisualizerState = this.visualizer.state
    this.buttons = [...this.element.querySelectorAll(SELECTORS.checkbox)]

    this.init()
  }

  /**
   * Initialize the component
   */
  init() {
    this.getSkipLink(this.element)

    this.configurePlayerControls()

    // Set visualizer blending mode and scene rotation
    this.visualizer.setBlendingMode('Difference')
    this.rotateOnScroll()

    this.setupIntersectionObservers()

    // For each sound object, create and add an AudioTrack and add a
    // corresponding object to the SceneVisualizer
    this.getSoundMeta().forEach((meta, index) => {
      this.tracks.push(new AudioTrack(meta.path, { ...meta }))
      this.visualizer.addObject({
        palettes: meta.palettes,
        zoom: meta.zoom
      })
    })

    // Update track data and texture uniforms before each render frame
    this.visualizer.onBeforeRender = (vis, time, elapsedTime) => {
      this.tracks.forEach(track => track.update())
      vis.objects.forEach((object, index) => {
        object.material.uniforms.uData.value = this.tracks[index].data
        object.material.uniforms.uTexture.value = this.tracks[index].texture
        object.material.uniforms.time.value = time
        object.material.uniforms.timeDelta.value = elapsedTime
      })
    }

    // Removes the one-time use of fake data when the user initiates an audio
    // context state change, plays any tracks that are "checked", and then
    // unsets itself to only run once
    AudioTrack.onFirstInteraction = () => {
      if (this.audioButton.state === 'mute') {
        this.buttons.forEach((button, index) => {
          if (typeof this.visualizer.objects[index] === 'undefined') return
          this.visualizer.objects[index].material.animateUnsetFakeData()
        })
      }

      this.tracks.forEach((track, index) => {
        if (typeof this.buttons[index] === 'undefined') return
        if (this.buttons[index].checked) {
          track.play()
        }
      })
    }

    // For each button: set change event handlers, check initial state and set
    // the apropriate visualizer and AudioTrack settings
    this.buttons.forEach((button, index) => {
      if (typeof this.tracks[index] === 'undefined') return
      const track = this.tracks[index]

      button.addEventListener('change', (evt) => {
        this.handleButtonChange(evt.target, track, this.visualizer.objects[index].material)
      })

      if (button.checked) {
        if (AudioTrack.contextState !== 'running') {
          if (typeof this.visualizer.objects[index] === 'undefined') return
          this.visualizer.objects[index].material.setFakeData()
        } else {
          track.mute()
          track.play()
        }
      }
    })

    // If prefers-reduced-motion becomes active, pause visualizer motion
    this.emitter.on(GLOBAL_CONSTANTS.EVENTS.REDUCED_MOTION_CHANGE, (prefersReducedMotion) => {
      if (prefersReducedMotion) {
        this.visualizer.pause()
        this.motionButton.setState('play')
      }
    })

    document.addEventListener('visibilitychange', this.handleHibernationState)
  }

  /**
   * Sets up and configures actions for the mute and motion toggles.
   */
  configurePlayerControls() {
    // Toggle button state
    this.audioButton.onClick = (state, elem, obj) => {
      obj.setState(state === 'play' ? 'mute' : 'play')
      this.firstInteraction = false
    }

    // Toggle button state
    this.motionButton.onClick = (state, elem, obj) => {
      obj.setState(state === 'play' ? 'pause' : 'play')
    }

    // Set initial states
    // Do this BEFORE adding state change listeners
    this.audioButton.setState('play')
    this.motionButton.setState(this.hasReducedMotion ? 'play' : 'pause')

    // React to button state change
    // Set audio mute state and show/hide icons
    this.audioButton.onStateChange = (state) => {
      if (state === 'play') {
        this.audioButton.element.ariaPressed = false
        AudioTrack.muteAll()
      } else if (state === 'mute') {
        this.audioButton.element.ariaPressed = true
        if (AudioTrack.contextState !== 'running') {
          this.buttons.forEach((button, index) => {
            if (typeof this.tracks[index] === 'undefined') return
            const track = this.tracks[index]

            if (button.checked) {
              track.play()
            }
          })
        }
        AudioTrack.unmuteAll()
        this.stopOtherAudio()
      }

      this.updateAudioButtonDisplay(state)
    }

    // React to button state change
    // Set motion state and show/hide icons
    this.motionButton.onStateChange = (state, elem) => {
      if (state === 'play') {
        this.motionButton.element.ariaPressed = false
        this.visualizer.pause()
      } else if (state === 'pause') {
        this.motionButton.element.ariaPressed = true
        this.visualizer.unpause()
      }

      this.updateMotionButtonDisplay(state)
    }

    // Set initial button display
    this.updateAudioButtonDisplay('play')
    this.updateMotionButtonDisplay(this.hasReducedMotion ? 'play' : 'pause')
  }

  /**
   * Creates IntersectionObservers for the element.
   *
   * The first IntersectionObserver delays running the WebGL instance until the
   * user is close to the component. It only runs once. After running, it sets
   * up the second observer which adds and removes event listeners to help
   * free up resources when the component is out of view.
   */
  setupIntersectionObservers() {
    // This is to reduce strain on the browser by making sure certain processes
    // (resize handling, motion) only run when the component is in view.
    const mainObserver = new IntersectionObserver((entries, observer) => {
      entries.forEach(entry => {
        this.isVisible = entry.isIntersecting
        if (entry.isIntersecting) {
          if (this.prevVisualizerState === SceneVisualizer.State.PLAYING) {
            this.visualizer.unpause()
          }
          document.addEventListener('scroll', this.rotateOnScroll)
          document.addEventListener('resize', this.rotateOnScroll)
          document.addEventListener('visibilitychange', this.handleDocumentVisibility)
          if ('orientation' in screen) {
            screen.orientation.addEventListener('change', this.handleScreenOrientationChange)
          }
        } else {
          this.prevVisualizerState = this.visualizer.state
          this.visualizer.pause()
          document.removeEventListener('scroll', this.rotateOnScroll)
          document.removeEventListener('resize', this.rotateOnScroll)
          document.removeEventListener('visibilitychange', this.handleDocumentVisibility)
          if ('orientation' in screen) {
            screen.orientation.removeEventListener('change', this.handleScreenOrientationChange)
          }
        }
        this.handleHibernationState()
      })
    }, {
      rootMargin: '0px'
    })

    // Listens for the first time the component comes close to in-view and
    // starts the component at that moment. Registers the main Intersection
    // Observer above.
    const initObserver = new IntersectionObserver((entries, observer) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          this.visualizer.run()
          if (this.hasReducedMotion) {
            this.visualizer.pause()
          }
          initObserver.unobserve(this.element)
          mainObserver.observe(this.element)
        }
      })
    }, {
      rootMargin: '300px'
    })
    initObserver.observe(this.element)
  }

  /**
   * Gets the sound data to be passed into the AudioMaterial and AudioTrack
   * initializers.
   * @return {Object<String: Any>}
   */
  getSoundMeta() {
    return this.buttons.map(button => {
      const data = {
        ...button.dataset
      }

      if ('palettes' in data) {
        data.palettes = JSON.parse(data.palettes)
      }

      return data
    })
  }

  /**
   * Manages the visualizer state based on document visibility.
   */
  handleDocumentVisibility = () => {
    if (document.visibilityState === 'hidden') {
      this.prevVisualizerState = this.visualizer.state
      this.visualizer.pause()
    } else if (document.visibilityState === 'visible') {
      if (this.prevVisualizerState === SceneVisualizer.State.PLAYING) {
        this.visualizer.unpause()
      }
    }
  }

  /**
   * Reframes the component in view when a devices is rotated.
   * @param {Event} evt
   */
  handleScreenOrientationChange = (evt) => {
    setTimeout(() => {
      scroll({
        top: this.element.getBoundingClientRect().top + document.documentElement.scrollTop,
        behavior: 'instant'
      })
      this.rotateOnScroll()
    }, 500)
  }

  /**
   * Updates the audio button's visual display for the given state.
   * @param {String} state
   */
  updateAudioButtonDisplay(state) {
    this.audioButton.element.querySelectorAll('[data-decorator-index] [data-state]').forEach(decorator => {
      if (decorator.dataset.state === state) {
        decorator.classList.remove('decorator--hidden')
      } else {
        decorator.classList.add('decorator--hidden')
      }
    })
  }

  /**
   * Updates the motion button's visual display for the given state.
   * @param {String} state
   */
  updateMotionButtonDisplay(state) {
    this.motionButton.element.querySelectorAll('[data-decorator-index] [data-state]').forEach(decorator => {
      if (decorator.dataset.state === state) {
        decorator.classList.remove('decorator--hidden')
      } else {
        decorator.classList.add('decorator--hidden')
      }
    })
  }

  /**
   * Subltly rotates the 3D scene with the user's scroll position.
   */
  rotateOnScroll = () => {
    const { top, height } = this.element.getBoundingClientRect()
    let amount = (top + height) / (height + window.innerHeight)
    amount = 1 - Math.min(1, Math.max(0, amount))

    const distance = 60
    this.visualizer.setInteractiveRotation((amount - 0.5) * -distance)
  }

  /**
   * Finds the ID of the next logical element in the DOM tree and assigns the
   * value as an href to this.skipLink.
   * @param {Element} element
   */
  getSkipLink = (element) => {
    const next = element.nextElementSibling
    if (next !== null) {
      if (next.hasAttribute('id')) {
        this.skipLink.href = `#${next.id}`
      } else {
        this.getSkipLink(next)
      }
    } else {
      const parent = element.parentElement
      if (element.parentElement !== null) {
        this.getSkipLink(parent)
      } else {
        this.skipLink.remove()
      }
    }
  }

  get hasReducedMotion() {
    return document.documentElement.classList.contains('reduced-motion')
  }

  /**
   * Pauses any active Audio Player blocks on the page.
   */
  stopOtherAudio() {
    if (!('unlocked' in window && 'audioplayers' in window.unlocked)) return

    for (const player in window.unlocked.audioplayers) {
      window.unlocked.audioplayers[player].pause()
    }
  }

  /**
   * Handles changes in the audio button states.
   *
   * The main responsibility of this method is to play/pause the AudioTrack
   * passed to it, but based on application state it can also flip the state of
   * the audio toggle button, set or unset "fake" data (ambient animation), and
   * sleep or wake the AudioContext.
   *
   * @param {Element} button - An HTML checkbox element.
   * @param {AudioTrack} track - The associated AudioTrack.
   * @param {AudioMaterial} material - The associated AudioMaterial.
   */
  handleButtonChange = (button, track, material) => {
    if (this.audioButton.state === 'mute') {
      AudioTrack.unmuteAll()
    }

    const someChecked = this.buttons.some(b => b.checked)
    const noneChecked = !someChecked

    if (button.checked) {
      track.play()
    } else {
      track.pause()
      if (noneChecked) {
        material.animateSetFakeData()
        AudioTrack.hibernate()
      }
    }

    if (someChecked) {
      this.visualizer.objects.forEach(({ material }) => {
        material.animateUnsetFakeData()
      })
      AudioTrack.wake()
    }

    if (this.firstInteraction) {
      this.audioButton.setState('mute')
      this.firstInteraction = false
    }
  }

  /**
   * Updates the global AudioTrack hibernation state. This helps to save
   * resources when the AudioContext is not needed.
   */
  handleHibernationState = () => {
    const allMuted = this.tracks.every(t => t.muted)
    const someChecked = this.buttons.some(b => b.checked)

    if (document.visibilityState === 'hidden') {
      AudioTrack.hibernate()
    } else if (!this.isVisible && allMuted) {
      AudioTrack.hibernate()
    } else if (this.isVisible && document.visibilityState === 'visible' && someChecked) {
      AudioTrack.wake()
    }
  }
}

export default {
  selector: SELECTORS.visualizer,
  Source: Visualizer
}
